import {Alert, ComboboxParsedItem, Stack, Text} from '@mantine/core';
import {Field, useFormikContext} from 'formik';
import {
    FormikAutocomplete,
    FormikTextInput,
    PlatformFieldset,
} from '@flexinet/ui-components';
import {TbAlertTriangle} from 'react-icons/tb';
import {DeploymentAccount, ProvisionPipelineInput} from '../types';
import {
    deployRoleSuggestions,
    inferStageFromAccountName,
} from '../config/deploy-roles';

// FNP-1267: the deployment accounts were never on any form. Two of their fields
// decide whether a deploy can happen at all, and both were invisible:
//
//   deploymentBucketName — provisioning THROWS without it (pipeline-cft-builder.ts)
//   deployRoleArn        — used to fall back to the legacy CodePipelineCrossAccountRole,
//                          which exists in the older test/prod accounts and in NO dev
//                          account. A pipeline provisioned green and then failed its
//                          first deploy with an AccessDenied naming a role nobody had
//                          chosen. The fallback is gone; the field is now required.
//
// The accounts themselves come from the selected account group, which is a live
// projection of AWS Organizations — id, name and region are read-only here because
// Organizations owns them. The bucket and the role are OUR configuration: they have
// no upstream source, so they must be entered and must be visible.
export const DeploymentAccountsEditor = () => {
    const {values} = useFormikContext<ProvisionPipelineInput>();
    const accounts: DeploymentAccount[] = values.deploymentAccounts ?? [];

    // FNP-1242: the bucket and the role are required for an S3 deploy stage and
    // meaningless for a CloudFormation one — pipeline-cft-builder throws for
    // either only INSIDE `if (stage.deployProvider === 'S3')`.
    //
    // Both fields carried an unconditional asterisk, so provisioning a CDK API
    // repo showed a required Deployment Bucket that has no correct value: there
    // is no bucket, and the operator had to decide whether to invent one.
    // Nothing validated it either, since Mantine's withAsterisk renders a marker
    // and sets no `required` attribute — so the form accepted an empty value
    // while insisting it was mandatory. Worse, the asterisk on the role IS real
    // on the S3 path, so one of the two markers meant something.
    //
    // Absent stages mean the form is still being filled in, which is not the
    // same as "S3": defaulting to required there would restore the false
    // asterisk on every CDK pipeline.
    const hasS3DeployStage = (values.stages ?? [])
        .some(stage => stage.deployProvider === 'S3');

    if (accounts.length === 0) {
        return (
            <PlatformFieldset legend="Deployment Accounts">
                <Alert color="orange" icon={<TbAlertTriangle/>} my="md">
                    No deployment accounts resolved. Select an account group above —
                    without accounts, a deploy stage has nowhere to deploy to.
                </Alert>
            </PlatformFieldset>
        );
    }

    return (
        <PlatformFieldset legend="Deployment Accounts">
            <Stack gap="xl" my="xl">
                {accounts.map((account, index) => {
                    const missingRole = !account.deployRoleArn;
                    const missingBucket = !account.deploymentBucketName;
                    // Built from this account's own id, so the suggested ARN
                    // always matches the row it sits on. An ARN repeats its
                    // account id twice, which is exactly the sort of thing that
                    // is wrong-but-plausible when typed by hand across twelve
                    // rows.
                    const roleSuggestions = deployRoleSuggestions(
                        account.accountId,
                        account.accountName,
                        account.region,
                    );
                    const stage = inferStageFromAccountName(account.accountName);
                    return (
                        <PlatformFieldset
                            key={account.accountId ?? index}
                            legend={account.accountName
                                ? `${account.accountName} (${account.accountId})`
                                : account.accountId}
                        >
                            <Stack gap="md">
                                <Stack gap={4}>
                                    <Text size="sm" fw={500}>Account</Text>
                                    <Text size="sm">
                                        {account.accountId}
                                        {account.region ? ` · ${account.region}` : ''}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                        Read from AWS Organizations via the selected account
                                        group. Change it by selecting a different group.
                                    </Text>
                                </Stack>

                                <Field
                                    name={`deploymentAccounts.${index}.deploymentBucketName`}
                                    label="Deployment Bucket"
                                    description={hasS3DeployStage
                                        ? "S3 bucket this account's built site is synced into"
                                        : "Only used by an S3 deploy stage. This pipeline deploys through CloudFormation, so leave it empty."}
                                    component={FormikTextInput}
                                    withAsterisk={hasS3DeployStage}
                                />

                                <Field
                                    name={`deploymentAccounts.${index}.deployRoleArn`}
                                    label="Deploy Role ARN"
                                    description={
                                        `Role the pipeline assumes in ${stage ? `this ${stage.toUpperCase()} account` : 'this account'} to write the bundle. `
                                        + 'Pick a suggestion or type any ARN. '
                                        + 'CodePipelineCrossAccountRole is the one that works for an S3 site deploy; '
                                        + 'the cdk-hnb659fds role is for CloudFormation stages only.'
                                    }
                                    component={FormikAutocomplete}
                                    // ⚠️ TWO traps here, both found by mounting
                                    // this component in a test after the first
                                    // version reached production broken.
                                    //
                                    // 1. {id, value, label} objects, NOT bare
                                    //    strings. The wrapper reads `value` off
                                    //    each entry; strings throw "Cannot use
                                    //    'in' operator to search for 'value' in
                                    //    undefined" and the form renders as an
                                    //    application error.
                                    //
                                    // 2. label MUST equal value. Mantine shows
                                    //    the matching option's LABEL in the
                                    //    input, so a descriptive label makes a
                                    //    saved pipeline display prose where its
                                    //    ARN should be -- and re-saving would
                                    //    write that prose as the deploy role.
                                    //    The explanation lives in `description`
                                    //    below, where it cannot be mistaken for
                                    //    a value.
                                    data={roleSuggestions.map(arn => ({
                                        id: arn,
                                        value: arn,
                                        label: arn,
                                    }))}
                                    placeholder={roleSuggestions[0]}
                                    // ⚠️ Show every suggestion regardless of
                                    // what is in the field. Autocomplete filters
                                    // options by substring against the current
                                    // value by default, so a row already holding
                                    // the CDK ARN hid the legacy option — the
                                    // one the operator is there to switch TO.
                                    // This field exists to CHANGE a stored
                                    // value, so filtering by that value is
                                    // backwards. Two options; nothing to scroll.
                                    filter={({options}: {options: ComboboxParsedItem[]}) => options}
                                    withAsterisk={hasS3DeployStage}
                                />

                                {/* Gated on the S3 path for the same reason as the
                                    asterisks: on a CloudFormation pipeline this
                                    warned about two fields that are never read,
                                    which reads as "you have not finished" when
                                    the form is in fact complete. */}
                                {hasS3DeployStage && (missingRole || missingBucket) && (
                                    <Alert color="orange" icon={<TbAlertTriangle/>}>
                                        {missingRole && missingBucket
                                            ? 'This account has no deploy role and no deployment bucket. '
                                            : missingRole
                                                ? 'This account has no deploy role. '
                                                : 'This account has no deployment bucket. '}
                                        Provisioning an S3 deploy stage against it will be
                                        rejected until both are set.
                                    </Alert>
                                )}
                            </Stack>
                        </PlatformFieldset>
                    );
                })}
            </Stack>
        </PlatformFieldset>
    );
};
